/**
 * useContainerOperations Hook for UI-Model-1
 *
 * Provides container CRUD operations with command dispatch.
 * Supports nested containers and parent-child relationships.
 *
 * Uses UIModelStateContext (API-Direct approach) where state.model
 * contains the nested API structure.
 */

import { useCallback } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { useUIModelState } from '../contexts/UIModelStateContext';
import { useSelection } from '../contexts/SelectionContext';
import {
  ContainerAPI,
  ItemAPI,
  FieldType,
  DEFAULT_CONTAINER_CONFIGURATION,
} from '../types/state.types';
import {
  createFieldAPI,
  createCategoryAPI,
  createConnectorAPI,
  createSubmodelFullAPI,
  buildDropDataConfiguration,
  isSubmodelFull,
  SubmodelFullAPI,
  UsedComponentAPI,
} from '../types/api.types';
import { createFieldConfig } from '../config/fieldDefaults';
import { createCategoryConfig } from '../config/fieldDefaults/category.config';
import {
  createAddContainerCommand,
  createRemoveContainerCommand,
  createUpdateContainerCommand,
  createMoveContainerCommand,
  createDuplicateContainerCommand,
  createAddItemCommand,
  createRemoveItemCommand,
  createMoveItemCommand,
  createUpdateItemCommand,
  createBatchCommand,
} from '../types/command.types';
import { calculateWidthsOnAdd, createWidthUpdates, parseWidth, getEqualWidths } from '../container-management/utils/widthCalculations';
import { getContainersInRow } from '../container-management/utils/rowCalculations';
import { CONTAINER_CONSTRAINTS } from '../container-management/constants/constraints';
import { generateId } from '../utils/idGenerator';
import { getContainerDirectLock } from '../container-management/validators/submodelLockValidator';
import { isFreestyle } from '../selectors';
// Note: Validation is done inline using API-direct format, not via containerHelpers
// which expects NormalizedState. This follows the API-direct architecture principle.

// ============================================================================
// Types
// ============================================================================

/**
 * Placement mode for container duplication
 * - 'same-row': Adds duplicate right after source in same row (if space available)
 * - 'new-row': Creates a new row below source container's row
 */
export type DuplicatePlacement = 'same-row' | 'new-row';

/**
 * Layout helpers passed to duplicateContainer for smart naming and placement
 */
export interface DuplicateLayoutHelpers {
  getNextContainerNumber: () => number;
  getNestedContainerName: (parentId: string) => string;
  canAddToRowIndex: (rowIndex: number) => boolean;
  canAddToNestedRow: (parentId: string, rowIndex: number) => boolean;
}

// ============================================================================
// Hook
// ============================================================================

export const useContainerOperations = (canvasId?: string) => {
  const {
    state,
    dispatch,
    getContainer,
    getItem,
    getContainersForCanvas,
    getItemsForContainer,
    getChildContainers,
    isFieldUsed,
    isCategoryUsed,
    isConnectorUsed,
  } = useUIModelState();
  const { selectContainer, selectItem, clearSelection } = useSelection();

  // ============================================================================
  // Container Operations
  // ============================================================================

  const addContainer = useCallback((options: {
    name?: string;
    parentId?: string;
    insertIndex?: number;
    configuration?: Partial<ContainerAPI['configuration']>;
  } = {}) => {
    // Compute active canvas ID from editorState (groupId_pageId format)
    const activeCanvasId = state.editorState.activeGroupId && state.editorState.activePageId
      ? `${state.editorState.activeGroupId}_${state.editorState.activePageId}`
      : null;
    const targetCanvasId = canvasId || activeCanvasId;

    if (!targetCanvasId) {
      console.warn('No canvas ID available for container creation');
      return null;
    }

    const containerId = generateId('container');
    const currentContainers = getContainersForCanvas(targetCanvasId);
    const containerName = options.name || `Container ${currentContainers.length + 1}`;

    const command = createAddContainerCommand(
      {
        container: {
          id: containerId,
          name: containerName,
          configuration: {
            ...DEFAULT_CONTAINER_CONFIGURATION,
            ...options.configuration,
          },
        } as ContainerAPI,
        parentContainerId: options.parentId,
        canvasId: targetCanvasId,
        insertIndex: options.insertIndex,
      },
      `Add container "${containerName}"`
    );

    dispatch({ type: 'EXECUTE_COMMAND', payload: command });

    // Select the new container
    selectContainer(containerId);

    return containerId;
  }, [canvasId, state, dispatch, selectContainer, getContainersForCanvas]);

  const removeContainer = useCallback((containerId: string) => {
    const { element: container, path } = getContainer(containerId);
    if (!container) {
      console.warn(`Container ${containerId} not found`);
      return;
    }

    // Get canvas ID from path
    const containerCanvasId = path?.canvasId;
    if (!containerCanvasId) {
      console.warn(`Could not determine canvas ID for container ${containerId}`);
      return;
    }

    // Collect items to remove for undo
    const collectItems = (cId: string): ItemAPI[] => {
      const items: ItemAPI[] = [];
      const containerItems = getItemsForContainer(cId);
      items.push(...containerItems);
      // Recursively collect from children
      const childContainers = getChildContainers(cId);
      childContainers.forEach(child => {
        items.push(...collectItems(child.id));
      });
      return items;
    };

    // Collect descendants for undo
    const collectDescendants = (cId: string): ContainerAPI[] => {
      const descendants: ContainerAPI[] = [];
      const children = getChildContainers(cId);
      children.forEach(child => {
        descendants.push(child);
        descendants.push(...collectDescendants(child.id));
      });
      return descendants;
    };

    const command = createRemoveContainerCommand(
      {
        containerId,
        canvasId: containerCanvasId,
        removedContainer: container,
        removedItems: collectItems(containerId),
        removedChildContainers: collectDescendants(containerId),
      },
      `Remove container "${container.name}"`
    );

    dispatch({ type: 'EXECUTE_COMMAND', payload: command });

    // Clear selection if removed container was selected
    clearSelection();
  }, [state, dispatch, getContainer, getItemsForContainer, getChildContainers, clearSelection]);

  const updateContainer = useCallback((
    containerId: string,
    updates: Partial<ContainerAPI>
  ) => {
    const { element: container, path } = getContainer(containerId);
    if (!container) {
      console.warn(`Container ${containerId} not found`);
      return;
    }

    const containerCanvasId = path?.canvasId;
    if (!containerCanvasId) {
      console.warn(`Could not determine canvas ID for container ${containerId}`);
      return;
    }

    const command = createUpdateContainerCommand(
      {
        containerId,
        canvasId: containerCanvasId,
        updates: {
          name: updates.name,
          configuration: updates.configuration,
          submodel: updates.content?.submodel,
          submodels: updates.content?.submodels,
          items: updates.content?.items,
        },
        previousValues: container,
      },
      `Update container "${container.name}"`
    );

    dispatch({ type: 'EXECUTE_COMMAND', payload: command });
  }, [state, dispatch, getContainer]);

  const moveContainer = useCallback((
    containerId: string,
    toIndex: number,
    toParentId?: string | null,
    toCanvasId?: string
  ) => {
    const { element: container, path, parent } = getContainer(containerId);
    if (!container) {
      console.warn(`Container ${containerId} not found`);
      return;
    }

    const containerCanvasId = path?.canvasId;
    const fromParentId = parent?.id || container.metadata?.parentContainerId;

    const command = createMoveContainerCommand(
      {
        containerId,
        fromIndex: container.order,
        fromParentId: fromParentId ?? null,
        fromCanvasId: containerCanvasId ?? '',
        toIndex,
        toParentId: toParentId ?? null,
        toCanvasId: toCanvasId ?? containerCanvasId ?? '',
      },
      `Move container "${container.name}"`
    );

    dispatch({ type: 'EXECUTE_COMMAND', payload: command });
  }, [state, dispatch, getContainer]);

  /**
   * Deep clones a container with new IDs for container and all children
   * @param source - The source container to clone
   * @param newName - The name for the cloned container
   * @param newParentId - Optional parent ID for nested containers
   * @param newRowIndex - Optional row index for the clone
   * @param newOrder - Optional order within the row
   */
  const deepCloneContainerWithNewIds = useCallback((
    source: ContainerAPI,
    newName: string,
    newParentId?: string,
    newRowIndex?: number,
    newOrder?: number
  ): ContainerAPI => {
    const newContainerId = generateId('container');

    // Recursively clone nested containers with proper naming
    // Extract base name for nested containers (e.g., "Container 4" -> "4")
    const baseMatch = newName.match(/Container ([\d.]+)$/);
    const basePath = baseMatch ? baseMatch[1] : '1';

    const clonedNestedContainers = (source.content?.nestedContainers || []).map((nested, index) => {
      const nestedName = `Container ${basePath}.${index + 1}`;
      return deepCloneContainerWithNewIds(nested, nestedName, newContainerId);
    });

    // Use provided rowIndex/order or fall back to source values
    const finalRowIndex = newRowIndex ?? source.rowIndex;
    const finalOrder = newOrder ?? (source.order + 1);

    return {
      ...source,
      id: newContainerId,
      name: newName,
      configuration: { ...source.configuration, widthLocked: false },
      rowIndex: finalRowIndex,
      order: finalOrder,
      content: {
        items: [],                  // STRIPPED: no items in duplicate
        submodel: undefined,        // STRIPPED: no submodel binding
        nestedContainers: clonedNestedContainers.length > 0 ? clonedNestedContainers : undefined,
      },
      metadata: source.metadata ? {
        ...source.metadata,
        parentContainerId: newParentId,
        rowIndex: finalRowIndex,
      } : undefined,
      usageInfo: { usedComponents: [] },  // STRIPPED: clean usage tracking
    };
  }, []);

  /**
   * Duplicates a container with smart naming and row-aware placement
   * @param containerId - The ID of the container to duplicate
   * @param placement - Where to place the duplicate ('same-row' or 'new-row')
   * @param layoutHelpers - Helper functions from useContainerLayout for naming and validation
   * @returns The new container ID or null if duplication failed
   */
  const duplicateContainer = useCallback((
    containerId: string,
    placement: DuplicatePlacement,
    layoutHelpers: DuplicateLayoutHelpers
  ): string | null => {
    const { element: sourceContainer, path } = getContainer(containerId);
    if (!sourceContainer) {
      console.warn(`Container ${containerId} not found`);
      return null;
    }

    const containerCanvasId = path?.canvasId;
    if (!containerCanvasId) {
      console.warn(`Could not determine canvas ID for container ${containerId}`);
      return null;
    }

    // Determine new name based on container type (nested vs top-level)
    const isNested = !!sourceContainer.metadata?.parentContainerId;
    let newName: string;

    if (isNested) {
      // For nested containers: use getNestedContainerName(parentId)
      newName = layoutHelpers.getNestedContainerName(sourceContainer.metadata!.parentContainerId!);
    } else {
      // For top-level containers: use getNextContainerNumber()
      newName = `Container ${layoutHelpers.getNextContainerNumber()}`;
    }

    // Determine row placement based on mode
    const sourceRowIndex = sourceContainer.rowIndex ?? 0;
    let newRowIndex: number;
    let newOrder: number;
    let newWidth: string | undefined;

    // Width map for same-row redistribution (declared outside branch for dispatch logic)
    let widthMap: Map<string, string> | null = null;
    let rowSiblings: ContainerAPI[] = [];
    let rowShiftCommands: ReturnType<typeof createUpdateContainerCommand>[] = [];
    let overflowUnlock = false;
    let lockedSiblings: ContainerAPI[] = [];

    if (placement === 'same-row') {
      // Validate row has space - use appropriate check based on container type
      const rowHasSpace = isNested
        ? layoutHelpers.canAddToNestedRow(sourceContainer.metadata!.parentContainerId!, sourceRowIndex)
        : layoutHelpers.canAddToRowIndex(sourceRowIndex);

      if (!rowHasSpace) {
        console.warn('Cannot add to same row - row is full');
        return null;
      }
      newRowIndex = sourceRowIndex;
      newOrder = sourceContainer.order + 1;

      // Get siblings in the row and calculate redistributed widths
      rowSiblings = isNested
        ? (getContainer(sourceContainer.metadata!.parentContainerId!).element
            ?.content?.nestedContainers || [])
            .filter(c => (c.rowIndex ?? 0) === sourceRowIndex)
        : getContainersInRow(getContainersForCanvas(containerCanvasId), sourceRowIndex);

      const tempDuplicateId = '__temp_duplicate__';
      const sourceWidth = sourceContainer.configuration?.width || '100%';

      // Duplicate always copies the source container's width.
      // Other siblings (not the source) are redistributed in the remaining space.
      // Locked siblings keep their width; unlocked siblings share the leftover.
      widthMap = new Map<string, string>();

      const sourceWidthNum = parseWidth(sourceWidth);
      // Source keeps its width, duplicate copies it
      widthMap.set(sourceContainer.id, sourceWidth);
      widthMap.set(tempDuplicateId, sourceWidth);

      // Siblings excluding the source container
      const otherSiblings = rowSiblings.filter(c => c.id !== sourceContainer.id);

      if (otherSiblings.length > 0) {
        lockedSiblings = otherSiblings.filter(c => c.configuration?.widthLocked);
        const unlockedOthers = otherSiblings.filter(c => !c.configuration?.widthLocked);
        const lockedTotal = lockedSiblings.reduce(
          (sum, c) => sum + parseWidth(c.configuration?.width || '0'), 0
        );
        const remainingSpace = 100 - sourceWidthNum * 2 - lockedTotal;

        // Overflow: not enough space for unlocked siblings at minimum width
        if (remainingSpace < CONTAINER_CONSTRAINTS.MIN_WIDTH_PERCENT * unlockedOthers.length
            || remainingSpace < 0) {
          // Fall back: redistribute ALL containers equally, remove locks
          widthMap = calculateWidthsOnAdd(rowSiblings, tempDuplicateId, 'equal');
          overflowUnlock = lockedSiblings.length > 0;
          if (overflowUnlock) {
            toast.info('Width locks removed to fit the duplicate');
          }
        } else {
          // Locked siblings keep their width
          for (const c of lockedSiblings) {
            widthMap.set(c.id, c.configuration?.width || '33%');
          }
          // Unlocked siblings share remaining space equally
          if (unlockedOthers.length > 0) {
            const perUnlocked = Math.floor(remainingSpace / unlockedOthers.length);
            const sortedUnlocked = [...unlockedOthers].sort((a, b) => a.order - b.order);
            sortedUnlocked.forEach((c, i) => {
              // Last unlocked gets remainder to ensure total = 100%
              const w = i === sortedUnlocked.length - 1
                ? remainingSpace - perUnlocked * (sortedUnlocked.length - 1)
                : perUnlocked;
              widthMap!.set(c.id, `${w}%`);
            });
          }
        }
      }

      newWidth = widthMap.get(tempDuplicateId) || sourceContainer.configuration?.width;
    } else {
      // new-row: create row below — copy source container's width
      newRowIndex = sourceRowIndex + 1;
      newOrder = 0;
      newWidth = sourceContainer.configuration?.width || '100%';

      const allContainers = isNested
        ? (getContainer(sourceContainer.metadata!.parentContainerId!).element
            ?.content?.nestedContainers || [])
        : getContainersForCanvas(containerCanvasId);

      const containersToShift = isNested
        ? allContainers.filter((c) => (c.rowIndex ?? 0) > sourceRowIndex)
        : allContainers.filter((c) => (c.rowIndex ?? 0) > sourceRowIndex && !c.metadata?.isNested);

      if (containersToShift.length > 0) {
        rowShiftCommands = containersToShift.map((container) =>
          createUpdateContainerCommand(
            {
              containerId: container.id,
              canvasId: containerCanvasId,
              updates: {
                rowIndex: (container.rowIndex ?? 0) + 1,
                metadata: container.metadata
                  ? { ...container.metadata, rowIndex: (container.rowIndex ?? 0) + 1 }
                  : undefined,
              },
            },
            `Shift row: ${container.rowIndex} → ${(container.rowIndex ?? 0) + 1}`
          )
        );
      }
    }

    const duplicatedContainer = deepCloneContainerWithNewIds(
      sourceContainer,
      newName,
      sourceContainer.metadata?.parentContainerId,
      newRowIndex,
      newOrder
    );

    // Update width configuration
    if (newWidth && duplicatedContainer.configuration) {
      duplicatedContainer.configuration.width = newWidth;
    }

    const duplicateCommand = createDuplicateContainerCommand(
      {
        sourceContainerId: containerId,
        canvasId: containerCanvasId,
        parentContainerId: sourceContainer.metadata?.parentContainerId,
        duplicatedContainer,
        duplicatedContainerId: duplicatedContainer.id,
        placement, // Pass placement mode to command for undo/redo handling
      },
      `Duplicate container "${sourceContainer.name}" → "${newName}"`
    );

    // For same-row: batch the duplicate with sibling width updates
    if (placement === 'same-row' && widthMap) {
      const widthUpdates = createWidthUpdates(rowSiblings, widthMap);
      const widthUpdateCommands = widthUpdates
        .filter(wu => wu.containerId !== duplicatedContainer.id && wu.containerId !== '__temp_duplicate__')
        .map(wu => {
          // When overflow triggers, also unlock previously locked containers
          const unlockConfig = overflowUnlock && lockedSiblings.some(ls => ls.id === wu.containerId)
            ? { widthLocked: false }
            : {};
          return createUpdateContainerCommand(
            {
              containerId: wu.containerId,
              canvasId: containerCanvasId,
              updates: { configuration: { width: wu.newWidth, ...unlockConfig } },
            },
            `Update width: ${wu.previousWidth} → ${wu.newWidth}`
          );
        });

      if (widthUpdateCommands.length > 0) {
        const batchCmd = createBatchCommand(
          [...widthUpdateCommands, duplicateCommand],
          `Duplicate "${sourceContainer.name}" with width redistribution`
        );
        dispatch({ type: 'EXECUTE_COMMAND', payload: batchCmd });
      } else {
        dispatch({ type: 'EXECUTE_COMMAND', payload: duplicateCommand });
      }
    } else if (rowShiftCommands.length > 0) {
      const batchCmd = createBatchCommand(
        [...rowShiftCommands, duplicateCommand],
        `Duplicate "${sourceContainer.name}" in new row with row shifts`
      );
      dispatch({ type: 'EXECUTE_COMMAND', payload: batchCmd });
    } else {
      dispatch({ type: 'EXECUTE_COMMAND', payload: duplicateCommand });
    }

    // Select the new container
    selectContainer(duplicatedContainer.id);

    return duplicatedContainer.id;
  }, [state, dispatch, getContainer, selectContainer, deepCloneContainerWithNewIds]);

  // ============================================================================
  // Item Operations
  // ============================================================================

  const addItem = useCallback((
    containerId: string,
    item: Partial<ItemAPI>,
    insertIndex?: number
  ) => {
    const { element: container, path } = getContainer(containerId);
    if (!container) {
      console.warn(`Container ${containerId} not found`);
      return null;
    }

    const containerCanvasId = path?.canvasId;
    if (!containerCanvasId) {
      console.warn(`Could not determine canvas ID for container ${containerId}`);
      return null;
    }

    const itemId = item.id || generateId('item');
    const containerItems = getItemsForContainer(containerId);
    const itemType = item.type || 'widget';
    const newItem: ItemAPI = {
      id: itemId,
      type: itemType,
      name: item.name || 'New Item',
      order: insertIndex ?? containerItems.length,
      configuration: item.configuration || {},
      ...item,
    } as ItemAPI;

    const command = createAddItemCommand(
      {
        item: newItem,
        canvasId: containerCanvasId,
        containerId,
        insertIndex,
      },
      `Add ${newItem.type} "${newItem.name}"`
    );

    dispatch({ type: 'EXECUTE_COMMAND', payload: command });

    // Select the new item
    selectItem(itemId, newItem.type, containerId);

    return itemId;
  }, [state, dispatch, getContainer, getItemsForContainer, selectItem]);

  const removeItem = useCallback((itemId: string) => {
    const { element: item, path, parent: itemContainer } = getItem(itemId);
    if (!item) {
      console.warn(`Item ${itemId} not found`);
      return;
    }

    const containerCanvasId = path?.canvasId;
    if (!containerCanvasId) {
      console.warn(`Could not determine canvas ID for item ${itemId}`);
      return;
    }

    const containerId = itemContainer?.id || path?.containerPath[path.containerPath.length - 1] || '';

    const command = createRemoveItemCommand(
      {
        itemId,
        canvasId: containerCanvasId,
        containerId,
        removedItem: item,
        removedIndex: item.order ?? 0,
      },
      `Remove ${item.type} "${item.name || 'Item'}"`
    );

    dispatch({ type: 'EXECUTE_COMMAND', payload: command });

    clearSelection();
  }, [state, dispatch, getItem, clearSelection]);

  const updateItem = useCallback((
    itemId: string,
    updates: Partial<ItemAPI>
  ) => {
    const { element: item, path, parent: itemContainer } = getItem(itemId);
    if (!item) {
      console.warn(`Item ${itemId} not found`);
      return;
    }

    const containerCanvasId = path?.canvasId;
    if (!containerCanvasId) {
      console.warn(`Could not determine canvas ID for item ${itemId}`);
      return;
    }

    const containerId = itemContainer?.id || path?.containerPath[path.containerPath.length - 1] || '';

    const command = createUpdateItemCommand(
      {
        itemId,
        canvasId: containerCanvasId,
        containerId,
        updates,
        previousValues: item,
      },
      `Update ${item.type} "${item.name || 'Item'}"`
    );

    dispatch({ type: 'EXECUTE_COMMAND', payload: command });
  }, [state, dispatch, getItem]);

  const moveItem = useCallback((
    itemId: string,
    toContainerId: string,
    toIndex: number
  ) => {
    const { element: item, path: itemPath, parent: itemContainer } = getItem(itemId);
    if (!item) {
      console.warn(`Item ${itemId} not found`);
      return;
    }

    const fromContainerId = itemContainer?.id || itemPath?.containerPath[itemPath.containerPath.length - 1] || '';
    const fromIndex = item.order ?? 0;
    const fromCanvasId = itemPath?.canvasId;

    const { path: toPath } = getContainer(toContainerId);
    const toCanvasId = toPath?.canvasId;

    if (!fromCanvasId || !toCanvasId) {
      console.warn(`Could not determine canvas IDs for move operation`);
      return;
    }

    const command = createMoveItemCommand(
      {
        itemId,
        fromCanvasId,
        toCanvasId,
        fromContainerId,
        toContainerId,
        fromIndex,
        toIndex,
      },
      `Move ${item.type} "${item.name || 'Item'}"`
    );

    dispatch({ type: 'EXECUTE_COMMAND', payload: command });
  }, [state, dispatch, getItem, getContainer]);

  // ============================================================================
  // Submodel Operations
  // ============================================================================

  const addSubmodelToContainer = useCallback((
    containerId: string,
    submodelId: string,
    submodelName: string
  ) => {
    // API-direct validation: check container exists and doesn't have different submodel
    const { element: container } = getContainer(containerId);
    if (!container) {
      console.warn(`Cannot add submodel: Container ${containerId} not found`);
      return false;
    }

    // Free style: mixing submodels is allowed, so never block. The first-stamped
    // content.submodel stays (preview data-source hint); every additional
    // submodel is recorded in content.submodels so each gets its own header
    // bar and configuration.
    if (isFreestyle(state.model)) {
      const existing = container.content?.submodel;
      if (existing?.id) {
        const list = container.content?.submodels?.length
          ? container.content.submodels
          : isSubmodelFull(existing)
            ? [existing]
            : [];
        if (existing.id !== submodelId && !list.some((s) => s.id === submodelId)) {
          const added = createSubmodelFullAPI(submodelId, submodelName, {
            configuration: {
              showTitle: true,
              displayName: submodelName,
              fieldName: submodelName,
              hideIfNoProducts: false,
              exclusionType: 'hide',
              isVisible: true,
            },
          });
          updateContainer(containerId, {
            content: { submodels: [...list, added] },
          } as Partial<ContainerAPI>);
        }
        return true;
      }
    } else {
      // Rule 1/2: derive the lock from the container's OWN direct items, not the
      // cached content.submodel (which can be stale or propagated). A container with
      // no direct items of another submodel is free and re-stamps the new one — so a
      // container freed by removing its last item accepts a different submodel.
      const directLock = getContainerDirectLock(container);

      // Locked to a DIFFERENT submodel by its own items → block.
      if (directLock && directLock !== submodelId) {
        console.warn(`Cannot add submodel: Container already has a different submodel`);
        return false;
      }
    }

    // Already correctly bound → nothing to do. Checking the cached binding here
    // (not the direct lock) means a STALE or propagated binding that differs from
    // this submodel falls through and gets corrected by the update below.
    if (container.content?.submodel?.id === submodelId) return true;

    // Only pass the submodel field — do NOT spread container.content here.
    // Spreading stale content (from a pre-dispatch closure) would overwrite
    // items added by a concurrent ADD_ITEM command.
    const submodel = createSubmodelFullAPI(submodelId, submodelName, {
      configuration: {
        showTitle: true,
        displayName: submodelName,
        fieldName: submodelName,
        hideIfNoProducts: false,
        exclusionType: 'hide',
        isVisible: true,
      },
    });

    updateContainer(containerId, {
      // Free style also opens the submodels list (first entry mirrors the
      // singular binding); restricted JSON stays exactly as before.
      content: isFreestyle(state.model)
        ? { submodel, submodels: [submodel] }
        : { submodel },
    } as Partial<ContainerAPI>);

    // Propagate to nested containers as a preview data-source hint — but ONLY into
    // containers that have no direct submodel of their own. Never overwrite a
    // nested container locked to its own field's submodel (Rule 2: for max=1 nested
    // containers are independent — the unconditional overwrite was the source of the
    // "same submodel everywhere" corruption).
    const propagateToNested = (c: ContainerAPI) => {
      for (const nested of c.content?.nestedContainers || []) {
        if (getContainerDirectLock(nested) === null) {
          updateContainer(nested.id, {
            content: { submodel },
          } as Partial<ContainerAPI>);
        }
        propagateToNested(nested);
      }
    };
    propagateToNested(container);

    return true;
  }, [updateContainer, getContainer, state.model]);

  const removeSubmodelFromContainer = useCallback((containerId: string) => {
    const { element: container } = getContainer(containerId);
    if (!container) return;

    // Only pass the submodel field — do NOT spread container.content.
    // Same stale-closure guard as addSubmodelToContainer.
    updateContainer(containerId, {
      content: {
        submodel: undefined,
      },
    } as Partial<ContainerAPI>);
  }, [updateContainer, getContainer]);

  /**
   * Create a placeholder submodel for widgets added without a real submodel
   * Rule 3: If user adds widgets WITHOUT a submodel being set first,
   * create a placeholder/temporary submodel on the container
   */
  const createPlaceholderSubmodel = useCallback((containerId: string): SubmodelFullAPI | null => {
    const { element: container } = getContainer(containerId);
    if (!container) return null;

    // Don't create if container already has a submodel
    if (container.content?.submodel) {
      return container.content.submodel as SubmodelFullAPI;
    }

    // Create placeholder submodel
    const placeholderSubmodel = createSubmodelFullAPI(
      `placeholder-${generateId('submodel')}`,
      'Temporary',
      {
        isTemporary: true,
        configuration: {
          showTitle: false,
          isVisible: true,
        },
      }
    );

    return placeholderSubmodel;
  }, [getContainer]);

  /**
   * Add a submodel with all its available children to a container
   * Rule 1: Adds the submodel reference + ALL available fields/categories/connectors
   * Rule 4: If placeholder exists, replace it with real submodel and update existing items
   */
  const addSubmodelWithChildren = useCallback((
    containerId: string,
    submodelId: string,
    submodelName: string,
    children: Array<{
      id: string;
      type: 'field' | 'category' | 'connector';
      name: string;
      fieldType?: string;
      parentId: string;
      categoryId?: string;
      categoryName?: string;
      categoryType?: string;
      dataConfiguration?: Record<string, unknown>;
    }>
  ): boolean => {
    const { element: container, path } = getContainer(containerId);
    if (!container) {
      console.warn(`Cannot add submodel with children: Container ${containerId} not found`);
      return false;
    }

    const containerCanvasId = path?.canvasId;
    if (!containerCanvasId) {
      console.warn(`Could not determine canvas ID for container ${containerId}`);
      return false;
    }

    const existingSubmodel = container.content?.submodel;
    const isReplacingPlaceholder = existingSubmodel &&
      'isTemporary' in existingSubmodel &&
      existingSubmodel.isTemporary === true;

    // 1. Create the real submodel reference
    const newSubmodel: SubmodelFullAPI = createSubmodelFullAPI(
      submodelId,
      submodelName,
      {
        isTemporary: false,
        configuration: {
          showTitle: true,
          isVisible: true,
        },
      }
    );

    // 2. Update existing items' parentId if replacing placeholder
    let updatedItems = [...(container.content?.items || [])];
    if (isReplacingPlaceholder) {
      updatedItems = updatedItems.map(item => {
        // Only update items that have 'parentId' (actual items, not nested containers)
        if ('parentId' in item) {
          return {
            ...item,
            parentId: submodelId,
          };
        }
        return item;
      });
    }

    // 3. Create new items from children and add to container.
    // Defense (global uniqueness, Rule 5): skip any child already placed elsewhere so a
    // submodel-header drag can never duplicate a field/category/connector.
    const availableChildren = children.filter((child) => {
      if (child.type === 'field') return !isFieldUsed(child.name, child.parentId);
      if (child.type === 'category') return !isCategoryUsed(child.categoryId || '', child.parentId);
      if (child.type === 'connector') return !isConnectorUsed(child.name, child.parentId);
      return true;
    });
    const newUsedComponents: UsedComponentAPI[] = [];
    const existingItemCount = updatedItems.length;

    availableChildren.forEach((child, index) => {
      const itemId = child.id;
      let newItem: ItemAPI;

      switch (child.type) {
        case 'field': {
          const fieldTypeStr = (child.fieldType as FieldType) || 'text';
          const filteredDataConfig = buildDropDataConfiguration(fieldTypeStr, child.dataConfiguration);
          const defaultConfig = createFieldConfig(fieldTypeStr, child.name);
          newItem = createFieldAPI(
            itemId,
            child.name,
            fieldTypeStr,
            submodelId,
            {
              order: existingItemCount + index,
              dataConfiguration: filteredDataConfig,
              configuration: {
                ...defaultConfig,
                fieldName: child.name,
                fieldType: fieldTypeStr.toLowerCase().replace(/-/g, ''),
                displayName: child.name,
              },
            }
          );
          break;
        }

        case 'category': {
          const catDisplayConfig = createCategoryConfig(child.name);
          newItem = createCategoryAPI(
            itemId,
            child.name,
            child.categoryId || '',
            submodelId,
            {
              order: existingItemCount + index,
              configuration: {
                ...catDisplayConfig,
                categoryId: child.categoryId || '',
                categoryName: child.categoryName || child.name,
                categoryType: child.categoryType || '',
                fieldName: child.name,
                fieldType: 'category',
                displayName: child.name,
                wrapperName: child.name,
                id: itemId,
                titleText: `Select ${child.name}`,
              },
            }
          );
          break;
        }

        case 'connector':
          newItem = createConnectorAPI(
            itemId,
            child.name,
            submodelId,
            {
              order: existingItemCount + index,
            }
          );
          break;

        default:
          return; // Skip unknown types
      }

      updatedItems.push(newItem);

      // Track used component (use newItem.id which is catItemId for categories, itemId for others)
      newUsedComponents.push({
        id: newItem.id,
        type: child.type,
        originalComponentId: child.id,
        isReusable: true,
        parentId: submodelId,
      });
    });

    // 4. Build the updated usageInfo
    const existingUsedComponents = container.usageInfo?.usedComponents || [];
    const updatedUsedComponents = isReplacingPlaceholder
      ? existingUsedComponents.map(used => ({
          ...used,
          parentId: submodelId,
        }))
      : existingUsedComponents;

    // 5. Update container with new submodel, items, and usageInfo.
    // Free style: a mixed container keeps its FIRST-stamped binding as the preview
    // data-source hint — a later submodel header dropped in must not re-stamp it —
    // and records every binding in content.submodels (own header bar + config each).
    const freestyleMode = isFreestyle(state.model);
    const stampedSubmodel =
      freestyleMode && existingSubmodel && !isReplacingPlaceholder
        ? existingSubmodel
        : newSubmodel;
    let submodelsList: SubmodelFullAPI[] | undefined;
    if (freestyleMode) {
      const base = container.content?.submodels?.length
        ? container.content.submodels
        : existingSubmodel && isSubmodelFull(existingSubmodel) && !isReplacingPlaceholder
          ? [existingSubmodel]
          : [];
      submodelsList = base.some((s) => s.id === submodelId) ? base : [...base, newSubmodel];
    }
    updateContainer(containerId, {
      content: {
        ...container.content,
        submodel: stampedSubmodel,
        ...(freestyleMode ? { submodels: submodelsList } : {}),
        items: updatedItems,
      },
      usageInfo: {
        usedComponents: [...updatedUsedComponents, ...newUsedComponents],
      },
    } as Partial<ContainerAPI>);

    // 6. Propagate to nested containers as a preview data-source hint — ONLY into
    // containers with no direct submodel of their own; never overwrite a child
    // locked to its own submodel (Rule 2: nested containers are independent).
    const propagateToNested = (c: ContainerAPI) => {
      for (const nested of c.content?.nestedContainers || []) {
        if (getContainerDirectLock(nested) === null) {
          updateContainer(nested.id, {
            content: { submodel: stampedSubmodel },
          } as Partial<ContainerAPI>);
        }
        propagateToNested(nested);
      }
    };
    propagateToNested(container);

    return true;
  }, [getContainer, updateContainer, isFieldUsed, isCategoryUsed, isConnectorUsed, state.model]);

  /**
   * Check if any child item from a submodel is already used in the container
   * Rule 2: If ANY field/category/connector from a submodel is already in the container,
   * the "Add All" option for that submodel must be DISABLED
   */
  const hasSubmodelChildInContainer = useCallback((
    submodelId: string,
    containerId: string
  ): boolean => {
    const { element: container } = getContainer(containerId);
    if (!container) return false;

    const usedComponents = container.usageInfo?.usedComponents || [];

    // Check if any used component has this submodel as parentId
    return usedComponents.some(used => used.parentId === submodelId);
  }, [getContainer]);

  // ============================================================================
  // Validation Helpers
  // ============================================================================

  const canMoveItem = useCallback((
    itemId: string,
    toContainerId: string
  ): { canDrop: boolean; reason?: string } => {
    // API-direct validation
    const { element: item } = getItem(itemId);
    if (!item) {
      return { canDrop: false, reason: 'Item not found' };
    }

    const { element: targetContainer } = getContainer(toContainerId);
    if (!targetContainer) {
      return { canDrop: false, reason: 'Target container not found' };
    }

    // Basic validation passed - item and target exist
    return { canDrop: true };
  }, [getItem, getContainer]);

  const canAddSubmodel = useCallback((
    submodelId: string,
    containerId: string
  ): { canDrop: boolean; reason?: string } => {
    // API-direct validation
    const { element: container } = getContainer(containerId);
    if (!container) {
      return { canDrop: false, reason: 'Container not found' };
    }

    // Check if container already has a different submodel
    if (container.content?.submodel && container.content.submodel.id !== submodelId) {
      return { canDrop: false, reason: 'Container already has a different submodel' };
    }

    return { canDrop: true };
  }, [getContainer]);

  return {
    // Container operations
    addContainer,
    removeContainer,
    updateContainer,
    moveContainer,
    duplicateContainer,
    // Item operations
    addItem,
    removeItem,
    updateItem,
    moveItem,
    // Submodel operations
    addSubmodelToContainer,
    removeSubmodelFromContainer,
    addSubmodelWithChildren,
    createPlaceholderSubmodel,
    // Validation
    canMoveItem,
    canAddSubmodel,
    hasSubmodelChildInContainer,
  };
};

export default useContainerOperations;
